import { Fragment, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import { backendHOST } from '../../../../client-config/client-config';
import { logout } from '../../../../actions/auth';
import { withRouter } from '../../../hooks/withRouter';

const url = `${backendHOST}/api/scriptantiaAdmin`;
const urlEnd = '/StudentPayments';

const emptyForm = {
  id_diak: '',
  datum: '',
  descr: '',
  coupon_code: '',
};

const FormCouponUse = ({ auth }) => {
  const [couponUses, setCouponUses] = useState([]);
  const [students, setStudents] = useState([]);
  const [coupons, setCoupons] = useState([]);

  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.token,
  };

  const formatDateForInput = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().slice(0, 10);
  };

  const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    return Number(value);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [couponUseResponse, studentsResponse, couponsResponse] =
        await Promise.all([
          fetch(`${url}/CouponUse`, {
            headers: authHeaders,
          }),
          fetch(`${backendHOST}/api/auth/User?uType=D`, {
            headers: authHeaders,
          }),
          fetch(`${url}/Coupons`, {
            headers: authHeaders,
          }),
        ]);

      if (!couponUseResponse.ok) {
        throw new Error('Nem sikerült betölteni a kupon felhasználásokat.');
      }

      if (!studentsResponse.ok) {
        throw new Error('Nem sikerült betölteni a diákokat.');
      }

      if (!couponsResponse.ok) {
        throw new Error('Nem sikerült betölteni a kuponokat.');
      }

      const couponUseData = await couponUseResponse.json();
      const studentsData = await studentsResponse.json();
      const couponsData = await couponsResponse.json();

      setCouponUses(Array.isArray(couponUseData) ? couponUseData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : []);
      setCoupons(Array.isArray(couponsData) ? couponsData : []);
    } catch (err) {
      setError(err.message || 'Ismeretlen hiba történt betöltés közben.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStudentLabel = (studentId) => {
    const item = students.find(
      (student) => String(student.id) === String(studentId),
    );

    return item?.text || studentId || '';
  };

  const sortedCouponUses = useMemo(() => {
    return [...couponUses].sort((a, b) => {
      const dateA = a.datum ? new Date(a.datum).getTime() : 0;
      const dateB = b.datum ? new Date(b.datum).getTime() : 0;

      return dateB - dateA;
    });
  }, [couponUses]);

  const sortedCoupons = useMemo(() => {
    return [...coupons].sort((a, b) =>
      String(a.code || '').localeCompare(String(b.code || ''), 'hu'),
    );
  }, [coupons]);

  const filteredCouponUses = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return sortedCouponUses;
    }

    return sortedCouponUses.filter((item) =>
      [
        item.id_bizonylat,
        item.id_diak,
        getStudentLabel(item.id_diak),
        item.datum,
        item.descr,
        item.coupon_code,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }, [sortedCouponUses, searchText, students]);

  const handleCreateClick = () => {
    setFormData({
      ...emptyForm,
      datum: formatDateForInput(new Date()),
    });

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (saving) return;

    setDialogOpen(false);
    setFormData(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const normalizePayload = (data) => {
    return {
      id_diak: numberOrNull(data.id_diak),
      datum: data.datum || null,
      osszeg: 0,
      fiz_mod: 'KU',
      descr: data.descr || '',
      coupon_code: data.coupon_code || null,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const payload = normalizePayload(formData);

      const response = await fetch(`${url}${urlEnd}`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Nem sikerült rögzíteni a kupon felhasználást.');
      }

      setMessage('Kupon felhasználás rögzítve.');

      setDialogOpen(false);
      setFormData(emptyForm);

      await loadData();
    } catch (err) {
      setError(err.message || 'Mentés közben hiba történt.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent='space-between'
                spacing={2}
              >
                <Box>
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Kupon felhasználás
                  </Typography>

                  <Typography variant='body2' color='text.secondary'>
                    Diákokhoz kapcsolódó kuponfelhasználások rögzítése.
                  </Typography>
                </Box>

                <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                >
                  Új kuponfelhasználás
                </Button>
              </Stack>

              <TextField
                label='Keresés'
                size='small'
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{ maxWidth: 320 }}
              />

              {error && (
                <Alert severity='error' onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Diák</TableCell>
                        <TableCell>Felhasználás dátuma</TableCell>
                        <TableCell>Megjegyzés</TableCell>
                        <TableCell>Kupon kódja</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredCouponUses.map((item) => (
                        <TableRow key={item.id_bizonylat} hover>
                          <TableCell>{getStudentLabel(item.id_diak)}</TableCell>
                          <TableCell>
                            {formatDateForInput(item.datum) || '-'}
                          </TableCell>
                          <TableCell>{item.descr}</TableCell>
                          <TableCell>{item.coupon_code}</TableCell>
                        </TableRow>
                      ))}

                      {filteredCouponUses.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align='center'>
                            Nincs megjeleníthető kuponfelhasználás.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Új kuponfelhasználás</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id='coupon-use-student-label'>Diák</InputLabel>
              <Select
                labelId='coupon-use-student-label'
                label='Diák'
                value={formData.id_diak}
                onChange={(event) =>
                  handleFormChange('id_diak', event.target.value)
                }
              >
                {students.map((student) => (
                  <MenuItem key={student.id} value={String(student.id)}>
                    {student.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label='Felhasználás dátuma'
              type='date'
              value={formData.datum}
              onChange={(event) =>
                handleFormChange('datum', event.target.value)
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label='Megjegyzés'
              value={formData.descr}
              onChange={(event) =>
                handleFormChange('descr', event.target.value)
              }
              multiline
              minRows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id='coupon-use-code-label'>Kupon kódja</InputLabel>
              <Autocomplete
                options={sortedCoupons}
                value={
                  sortedCoupons.find(
                    (coupon) =>
                      String(coupon.code) === String(formData.coupon_code),
                  ) || null
                }
                getOptionLabel={(option) => option?.code || ''}
                isOptionEqualToValue={(option, value) =>
                  String(option.code) === String(value.code)
                }
                onChange={(event, selectedCoupon) =>
                  handleFormChange('coupon_code', selectedCoupon?.code || '')
                }
                renderInput={(params) => (
                  <TextField {...params} label='Kupon kódja' fullWidth />
                )}
              />
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            Mégsem
          </Button>

          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Fragment>
  );
};

FormCouponUse.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(withRouter(FormCouponUse));
